import type { RouteRecordRaw } from 'vue-router'
import { UserRoleCodes } from '@/types'

import managerProductsRoutes from '@/router/routes/manager/products'
import managerCatalogsRoutes from '@/router/routes/manager/catalogs'
import managerBrandsRoutes from '@/router/routes/manager/brands'
import managerMarketingRoutes from '@/router/routes/manager/marketing'
import managerHeaderAndFooterRoutes from '@/router/routes/manager/headerAndFooter'
import managerPostsRoutes from '@/router/routes/manager/posts'
import managerGiftCardsRoutes from '@/router/routes/manager/giftcards'
import managerDictionariesRoutes from '@/router/routes/manager/dictionaries'

import { checkRights } from '@/helpers'

export default [
  /**
   * Manager routes
   * */
  {
    path: '/',
    name: 'manager-home',
    component: () => import('../../views/Manager/ManagerView.vue'),
    beforeEnter: [checkRights],
    meta: {
      layout: 'TheManagerLayout',
      roles: [
        UserRoleCodes.Administrator,
        UserRoleCodes.Manager,
        UserRoleCodes.Salesman,
      ],
    },
    /**
     * ManagerView as wrapper (with tabs)
     * */
    children: [
      /**
       * Blocks
       * */
      {
        path: 'blocks',
        name: 'manager-blocks',
        component: () => import('../../views/Manager/Blocks/ManagerBlocks.vue'),
        beforeEnter: [checkRights],
        meta: {
          layout: 'TheManagerLayout',
          roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
        },
      },
      /**
       * Catalog: labels / filters
       * */
      {
        path: 'labels',
        name: 'manager-labels',
        component: () =>
          import('../../views/Manager/Catalog/ManagerCatalogLabels.vue'),
        beforeEnter: [checkRights],
        meta: {
          layout: 'TheManagerLayout',
          roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
        },
      },
      {
        path: 'filters',
        name: 'manager-filters',
        component: () =>
          import('../../views/Manager/Catalog/ManagerCatalogFilters.vue'),
        beforeEnter: [checkRights],
        meta: {
          layout: 'TheManagerLayout',
          roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
        },
      },
      /**
       * Catalogs
       * */
      ...managerCatalogsRoutes,
      /**
       * Products
       * */
      ...managerProductsRoutes,
      /**
       * Brands
       * */
      ...managerBrandsRoutes,
      /**
       * MoySklad
       * */
      {
        path: 'moysklad/brands',
        name: 'manager-moysklad-brands',
        component: () =>
          import('../../views/Manager/MoySklad/ManagerMoySkladBrands.vue'),
        beforeEnter: [checkRights],
        meta: {
          layout: 'TheManagerLayout',
          roles: [UserRoleCodes.Administrator],
        },
      },
      {
        path: 'moysklad/categories',
        name: 'manager-moysklad-categories',
        component: () =>
          import('../../views/Manager/MoySklad/ManagerMoySkladCategories.vue'),
        beforeEnter: [checkRights],
        meta: {
          layout: 'TheManagerLayout',
          roles: [UserRoleCodes.Administrator],
        },
      },
      /**
       * MoySkladSync
       * */
      {
        path: 'moysklad/sync',
        name: 'manager-moysklad-sync',
        component: () =>
          import('../../views/Manager/MoySkladSync/ManagerMoySkladSync.vue'),
        beforeEnter: [checkRights],
        meta: {
          layout: 'TheManagerLayout',
          roles: [UserRoleCodes.Administrator],
        },
      },
      /**
       * Header & Footer
       * */
      ...managerHeaderAndFooterRoutes,
      /**
       * Marketing
       * */
      ...managerMarketingRoutes,
      /**
       * Blog / Posts
       * */
      ...managerPostsRoutes,
      /**
       * Сертификаты
       * */
      ...managerGiftCardsRoutes,
      /**
       * Переводы - словарь
       * */
      ...managerDictionariesRoutes,
      {
        path: 'seo',
        name: 'manager-seo',
        component: () => import('../../views/Manager/ManagerSeo.vue'),
        beforeEnter: [checkRights],
        meta: {
          layout: 'TheManagerLayout',
          roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
        },
      },
    ],
  },
  /**
   * Blog / Posts / List
   * */
  {
    path: '/console/blog/posts',
    name: 'manager-posts',
    component: () => import('../../views/Manager/Blog/ManagerPostList.vue'),
    beforeEnter: [checkRights],
    meta: {
      layout: 'TheManagerLayout',
      roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
    },
  },
  /**
   * Подарочные сертификаты
   * */
  {
    path: '/console/giftcards',
    name: 'manager-giftcards',
    component: () => import('../../views/Manager/GiftCards/ManagerGiftCardList.vue'),
    beforeEnter: [checkRights],
    meta: {
      layout: 'TheManagerLayout',
      roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
    },
  },
  /**
   * Переводы - словарь
   * */
  {
    path: '/console/dictionary',
    name: 'manager-dictionaries',
    component: () => import('../../views/Manager/Dictionaries/ManagerDictionariesList.vue'),
    beforeEnter: [checkRights],
    meta: {
      layout: 'TheManagerLayout',
      roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
    },
  },
  /**
   * Users
   * */
  {
    path: '/console/users',
    name: 'manager-users',
    component: () => import('../../views/Manager/Users/ManagerUsers.vue'),
    beforeEnter: [checkRights],
    meta: {
      layout: 'TheManagerLayout',
      roles: [UserRoleCodes.Administrator],
    },
  },
  /**
   * Clients
   * */
  {
    path: '/console/clients',
    name: 'manager-clients',
    component: () =>
      import('../../views/Manager/Clients/ManagerClientList.vue'),
    beforeEnter: [checkRights],
    meta: {
      layout: 'TheManagerLayout',
      roles: [
        UserRoleCodes.Administrator,
        UserRoleCodes.Manager,
        UserRoleCodes.Salesman,
      ],
    },
  },
  {
    path: '/console/clients/:id',
    name: 'manager-clients-item',
    component: () =>
      import('../../views/Manager/Clients/ManagerClientItem.vue'),
    beforeEnter: [checkRights],
    meta: {
      layout: 'TheManagerLayout',
      roles: [
        UserRoleCodes.Administrator,
        UserRoleCodes.Manager,
        UserRoleCodes.Salesman,
      ],
    },
  },
  /**
   * Orders
   * */
  {
    path: '/console/orders',
    name: 'manager-orders',
    component: () => import('../../views/Manager/Orders/ManagerOrderList.vue'),
    beforeEnter: [checkRights],
    meta: {
      layout: 'TheManagerLayout',
      roles: [
        UserRoleCodes.Administrator,
        UserRoleCodes.Manager,
        UserRoleCodes.Salesman,
      ],
    },
  },
  {
    path: '/console/orders/:code',
    name: 'manager-orders-item',
    component: () => import('../../views/Manager/Orders/ManagerOrderItem.vue'),
    beforeEnter: [checkRights],
    meta: {
      layout: 'TheManagerLayout',
      roles: [
        UserRoleCodes.Administrator,
        UserRoleCodes.Manager,
        UserRoleCodes.Salesman,
      ],
    },
  },
  /**
   * Blocks
   * */
  {
    path: '/console/blocks/posts/:id?',
    name: 'manager-blocks-posts',
    component: () =>
      import('../../views/Manager/Blocks/ManagerBlocksPosts.vue'),
    beforeEnter: [checkRights],
    meta: {
      layout: 'TheManagerLayout',
      roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
    },
  },
  {
    path: '/console/blocks/banners/:type/:id?',
    name: 'manager-blocks-banners',
    component: () =>
      import('../../views/Manager/Blocks/ManagerBlocksBanners.vue'),
    beforeEnter: [checkRights],
    meta: {
      layout: 'TheManagerLayout',
      roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
    },
  },
  {
    path: '/console/blocks/products/:id?',
    name: 'manager-blocks-products',
    component: () =>
      import('../../views/Manager/Blocks/ManagerBlocksProducts.vue'),
    beforeEnter: [checkRights],
    meta: {
      layout: 'TheManagerLayout',
      roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
    },
  },
  {
    path: '/console/blocks/presets/:id?',
    name: 'manager-blocks-presets',
    component: () =>
      import('../../views/Manager/Blocks/ManagerBlocksPresets.vue'),
    beforeEnter: [checkRights],
    meta: {
      layout: 'TheManagerLayout',
      roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
    },
  },
  /**
   * Loyalty
   * */
  {
    path: '/console/loyalty',
    name: 'manager-loyalty',
    component: () => import('../../views/Manager/Loyalty/ManagerLoyalty.vue'),
    beforeEnter: [checkRights],
    meta: {
      layout: 'TheManagerLayout',
      roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
    },
  },
  /**
   * Import product images
   * */
  {
    path: '/console/import',
    name: 'manager-import-list',
    component: () => import('../../views/Manager/Import/ManagerImportList.vue'),
    beforeEnter: [checkRights],
    meta: {
      layout: 'TheManagerLayout',
      roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
    },
  },
  {
    path: '/console/import/:uuid',
    name: 'manager-import-item',
    component: () => import('../../views/Manager/Import/ManagerImportItem.vue'),
    beforeEnter: [checkRights],
    meta: {
      layout: 'TheManagerLayout',
      roles: [UserRoleCodes.Administrator, UserRoleCodes.Manager],
    },
  },
  /**
   * Other
   * */
  {
    path: '/console/login',
    name: 'manager-login',
    component: () => import('../../views/Manager/ManagerLogin.vue'),
    meta: {
      layout: 'TheDefaultFluidLayout',
    },
  },
  {
    path: '/:catchAll(.*)*',
    name: '404',
    component: () => import('../../views/ErrorPage.vue'),
    meta: {
      layout: 'TheDefaultFluidLayout',
    },
  },
] as RouteRecordRaw[]
